import { useEffect, useRef, useState } from "react";

import {
  rebuildLoomControlSurfaceGeometry,
  type LoomControlSurfaceAcceptedGeometry,
} from "../../api/loomControlSurfaceRebuild";
import { canonicalLoomDefinitionJson } from "./loomAcceptedDefinitionBinding";
import type { LoomAuthoringDocument, LoomPrimitiveDefinition } from "./loomAuthoringModel";
import type {
  LoomSha256Revision,
  LoomWingControlSurfaceCollection,
} from "./loomWingControlSurfaceContract";

type WingDefinition = Extract<LoomPrimitiveDefinition, { primitive: "wing" }>;

interface LoomControlGeometryInput {
  collection: LoomWingControlSurfaceCollection | null;
  definitionRevision: LoomSha256Revision | null;
  document: LoomAuthoringDocument;
  projectId: string;
  wing: WingDefinition | null;
}

export interface LoomControlGeometryState {
  accepted: LoomControlSurfaceAcceptedGeometry | null;
  error: string | null;
  requestedDefinitionRevision: LoomSha256Revision | null;
  staleDropCount: number;
  status: "unavailable" | "updating" | "accepted" | "error";
}

const EMPTY: LoomControlGeometryState = {
  accepted: null, error: null, requestedDefinitionRevision: null,
  staleDropCount: 0, status: "unavailable",
};

interface LoomControlGeometryEntry {
  requestIdentity: string | null;
  retentionScope: string | null;
  value: LoomControlGeometryState;
}

function retentionScope(input: LoomControlGeometryInput): string | null {
  if (!input.collection) return null;
  return canonicalLoomDefinitionJson({
    schema: "loom.control-surface-geometry-retention-scope/v1",
    projectId: input.projectId,
    documentId: input.document.documentId,
    wingId: input.collection.wingId,
  });
}

export function loomControlSurfaceGeometryRequestIdentity(input: LoomControlGeometryInput): string | null {
  const { collection, definitionRevision, wing } = input;
  if (!collection || !definitionRevision || !wing || !collection.controlSurfaces.length) return null;
  const { controlSurfaces: _controls, ...neutralWingDefinition } = wing;
  return canonicalLoomDefinitionJson({
    schema: "loom.control-surface-geometry-request-identity/v1",
    projectId: input.projectId,
    documentId: input.document.documentId,
    wingId: collection.wingId,
    definitionRevision,
    wingDefinitionRevision: collection.wingDefinitionRevision,
    neutralWingDefinition,
    controlSurfaceCollection: collection,
  });
}

export function useLoomControlSurfaceGeometry(input: LoomControlGeometryInput): LoomControlGeometryState {
  const requestIdentity = loomControlSurfaceGeometryRequestIdentity(input);
  const scope = retentionScope(input);
  const inputRef = useRef(input); inputRef.current = input;
  const [entry, setEntry] = useState<LoomControlGeometryEntry>({
    requestIdentity: null, retentionScope: null, value: EMPTY,
  });
  const requestRef = useRef(0);
  useEffect(() => {
    const request = ++requestRef.current;
    const controller = new AbortController();
    const { collection, definitionRevision, document, projectId, wing } = inputRef.current;
    if (!requestIdentity || !scope || !collection || !definitionRevision || !wing) {
      setEntry((current) => ({ requestIdentity: null, retentionScope: null,
        value: { ...EMPTY, staleDropCount: current.value.staleDropCount } }));
      return () => controller.abort();
    }
    setEntry((current) => ({ requestIdentity, retentionScope: scope, value: {
      accepted: current.retentionScope === scope ? current.value.accepted : null,
      error: null, requestedDefinitionRevision: definitionRevision,
      staleDropCount: current.value.staleDropCount, status: "updating",
    } }));
    void rebuildLoomControlSurfaceGeometry({
      collection, definitionRevision, document, projectId, signal: controller.signal, wing,
    }).then((accepted) => {
      if (request !== requestRef.current || controller.signal.aborted) {
        setEntry((current) => ({ ...current, value: {
          ...current.value, staleDropCount: current.value.staleDropCount + 1,
        } }));
        return;
      }
      setEntry((current) => ({ requestIdentity, retentionScope: scope, value: {
        accepted, error: null, requestedDefinitionRevision: definitionRevision,
        staleDropCount: current.value.staleDropCount, status: "accepted",
      } }));
    }).catch((reason: unknown) => {
      if (request !== requestRef.current || controller.signal.aborted) return;
      setEntry((current) => ({ ...current, requestIdentity, retentionScope: scope, value: {
        ...current.value, error: reason instanceof Error ? reason.message
          : "Control geometry evaluation failed; the change was not applied.",
        requestedDefinitionRevision: definitionRevision, status: "error",
      } }));
    });
    return () => controller.abort();
  }, [requestIdentity, scope]);
  if (!requestIdentity || !scope) {
    return { ...EMPTY, staleDropCount: entry.value.staleDropCount };
  }
  if (entry.retentionScope !== scope) {
    return { ...EMPTY, requestedDefinitionRevision: input.definitionRevision,
      staleDropCount: entry.value.staleDropCount, status: "updating" };
  }
  if (entry.requestIdentity !== requestIdentity) {
    return { accepted: entry.value.accepted, error: null,
      requestedDefinitionRevision: input.definitionRevision,
      staleDropCount: entry.value.staleDropCount, status: "updating" };
  }
  return entry.value;
}
